// common
import React, { memo } from 'react';

// style
import styled, { css } from 'styled-components';

import { getLayoutStyle } from 'styles/components/getLayoutStyle';

import Button from './Button';

/**
 * @desc 버튼 그룹 컴포넌트
 * @param {Array} button - 버튼 데이터 배열 (각 버튼의 속성 포함)
 * @param {string} className - 추가적인 클래스명
 * @param {boolean} isShow - 버튼 그룹의 보이기 여부
 * @param {string} layout - 버튼 정렬
 * @returns {JSX.Element} 버튼 그룹 컴포넌트
 */
const ButtonGroup = ({ button, className, isShow, layout }) => {
  return (
    <ButtonGroupStyle className={className || ''} layout={layout}>
      {button.filter(Boolean).map(
        (
          {
            icon,
            text,
            className,
            onClick,
            disabled,
            beforeIcon,
            tooltip,
            buttonStyle,
            variant,
            size,
            position,
            shape,
            gtm,
            afterIcon,
            href,
            download,
            target,
          },
          idx
        ) => (
          <Button
            icon={icon}
            key={idx}
            className={[className, isShow && 'is_show']
              .filter(Boolean)
              .join(' ')}
            onClick={onClick}
            disabled={disabled}
            beforeIcon={beforeIcon}
            buttonStyle={buttonStyle}
            tooltip={tooltip}
            variant={variant}
            size={size}
            position={position}
            shape={shape}
            gtm={{
              ...gtm,
              target: text,
            }}
            afterIcon={afterIcon}
            href={href}
            download={download}
            target={target}
          >
            {text}
          </Button>
        )
      )}
    </ButtonGroupStyle>
  );
};

// 버튼 그룹 스타일 객체 리터럴 방식
const buttonGroupStyles = {
  modal_footer_btn_group: css`
    display: flex;
    align-items: center;
    justify-content: center;

    button {
      min-width: 100px;
      padding: 0 16px;
      height: 38px;
      font-size: 14px;
      font-weight: 400;
    }
  `,
  // 대댓글, 게시물 수정/삭제 영역
  modify_delete_btn_group: css`
    display: flex;
    gap: 16px;

    button {
      font-weight: 300;
      font-size: 14px;
      color: ${({ theme }) => theme.color.gray500};

      &:hover {
        color: ${({ theme }) => theme.color.gray800};
      }
    }
  `,
  // web sample 모달 +, - 버튼 영역
  web_sample_count_group: css`
    gap: 0;
    .num_btn {
      height: 36px;
      input[type='number'] {
        width: 36px;
        text-align: center;
        font-size: 12px;
        border-top: 1px solid ${({ theme }) => theme.color.gray200};
        border-bottom: 1px solid ${({ theme }) => theme.color.gray200};
        border-radius: 0;
      }
      input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      /* Firefox  */
      input[type='number'] {
        -moz-appearance: textfield;
      }
    }
    .border_btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      background: ${({ theme }) => theme.color.white};
      border: 1px solid ${({ theme }) => theme.color.gray200};
      color: ${({ theme }) => theme.color.primaryDark};

      &.plus {
        border-radius: 0 4px 4px 0;
      }
      &.minus {
        border-radius: 4px 0 0 4px;
      }
    }
  `,
  // 에러 났을 때 모달 [메인으로][다시 불러오기] 영역
  error_content_group: css`
    .error_reload {
      width: 120px;
      svg {
        width: 25px;
      }
    }
  `,
};

// 버튼 그룹의 스타일링을 담당하는 스타일 컴포넌트
const ButtonGroupStyle = styled.div`
  display: flex; // 버튼들을 가로로 배치
  gap: 8px; // 버튼 사이의 간격 설정

  ${({ layout }) => getLayoutStyle(layout)}

  // 각 조건에 따른 버튼 그룹 스타일 적용
  ${({ className }) => buttonGroupStyles[className] || null}

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

export const ButtonGroupWrapper = styled.div`
  display: flex;
  gap: 8px;
`;
const MapActionButtonGroup = styled(ButtonGroup)`
  .language {
    width: max-content;
    padding: 0 5px;
    border: 1px solid transparent;
  }
`;
const MemoizedButtonGroup = memo(ButtonGroup);
MemoizedButtonGroup.MapAction = MapActionButtonGroup;

export default MemoizedButtonGroup;
